import { supabase, invokeFunction } from "@/integrations/supabase/client";

type OutlineSection = { title?: string; description?: string; topics?: string[]; duration?: string };

function normTitle(s: string) {
  return s.trim().toLowerCase();
}

function cloneJson<T>(v: T): T {
  return JSON.parse(JSON.stringify(v));
}

function getSectionsMut(outline: Record<string, unknown>): { sections: OutlineSection[]; useModules: boolean } {
  if (Array.isArray(outline.sections)) {
    return { sections: outline.sections as OutlineSection[], useModules: false };
  }
  if (Array.isArray(outline.modules)) {
    return { sections: outline.modules as OutlineSection[], useModules: true };
  }
  return { sections: [], useModules: false };
}

function writeSectionsMut(outline: Record<string, unknown>, sections: OutlineSection[], useModules: boolean) {
  if (useModules) {
    outline.modules = sections;
    delete outline.sections;
  } else {
    outline.sections = sections;
    delete outline.modules;
  }
}

async function saveOutlineOnly(projectId: string, outline: Record<string, unknown>): Promise<{ error?: string }> {
  const { data, error } = await invokeFunction("save-addie-outline", {
    body: { project_id: projectId, creation_outline: outline },
  });
  if (error) return { error: error.message };
  const syncError = data && typeof data === "object" ? (data as { syncError?: string }).syncError : undefined;
  if (syncError) return { error: syncError };
  return {};
}

type Sb = typeof supabase & { from: (name: string) => ReturnType<typeof supabase.from> };
const sb = supabase as Sb;

/**
 * Remove one topic from creation_outline and delete its skill node (when found).
 * Reindexes remaining nodes in the chapter so save-addie-outline sync stays consistent.
 */
export async function removeOutlineTopic(
  projectId: string,
  sectionIndex: number,
  topicIndex: number,
): Promise<{ error?: string }> {
  const { data: row, error: rowErr } = await supabase
    .from("addie_outlines")
    .select("id, creation_outline")
    .eq("project_id", projectId)
    .maybeSingle();
  if (rowErr || !row?.creation_outline) return { error: rowErr?.message || "No outline found" };

  const outline = cloneJson(row.creation_outline) as Record<string, unknown>;
  const { sections, useModules } = getSectionsMut(outline);
  if (sectionIndex < 0 || sectionIndex >= sections.length) return { error: "Invalid section" };
  const topics = Array.isArray(sections[sectionIndex].topics) ? [...(sections[sectionIndex].topics as string[])] : [];
  if (topicIndex < 0 || topicIndex >= topics.length) return { error: "Invalid topic" };

  const topicTitle = String(topics[topicIndex] ?? "").trim();
  topics.splice(topicIndex, 1);
  sections[sectionIndex] = { ...sections[sectionIndex], topics };
  writeSectionsMut(outline, sections, useModules);

  const { data: treeData, error: treeErr } = await invokeFunction("get-skill-tree", { body: { courseId: projectId } });
  if (treeErr) return { error: treeErr.message };

  const chapters = (((treeData?.chapters ?? []) as { id: string; order_index: number }[]) || [])
    .slice()
    .sort((a, b) => a.order_index - b.order_index);
  const nodes = (treeData?.nodes ?? []) as { id: string; title: string; chapter_id: string | null }[];

  const chapterId = chapters[sectionIndex]?.id ?? null;
  const nt = normTitle(topicTitle);

  let nodeToDelete: (typeof nodes)[0] | undefined;
  if (chapterId) {
    nodeToDelete = nodes.find((n) => n.chapter_id === chapterId && normTitle(n.title) === nt);
  } else {
    nodeToDelete = nodes.find((n) => normTitle(n.title) === nt);
  }

  if (nodeToDelete) {
    const { error: delErr } = await supabase.from("skill_nodes").delete().eq("id", nodeToDelete.id);
    if (delErr) return { error: delErr.message };
  }

  if (chapterId) {
    const newTopics = topics.map((t) => String(t ?? "").trim()).filter(Boolean);
    const topicNorms = newTopics.map(normTitle);
    const tset = new Set(topicNorms);

    const { data: chNodesRaw } = await supabase
      .from("skill_nodes")
      .select("id, title, position_index")
      .eq("chapter_id", chapterId)
      .order("position_index", { ascending: true });
    const chNodes = (chNodesRaw ?? []) as { id: string; title: string; position_index: number }[];

    for (const n of chNodes) {
      if (!tset.has(normTitle(n.title))) {
        const { error: e } = await supabase.from("skill_nodes").delete().eq("id", n.id);
        if (e) return { error: e.message };
      }
    }

    const { data: chNodes2Raw } = await supabase
      .from("skill_nodes")
      .select("id, title")
      .eq("chapter_id", chapterId)
      .order("position_index", { ascending: true });
    const chNodes2 = (chNodes2Raw ?? []) as { id: string; title: string }[];

    const used = new Set<string>();
    for (let j = 0; j < newTopics.length; j++) {
      const want = normTitle(newTopics[j]);
      const node = chNodes2.find((n) => normTitle(n.title) === want && !used.has(n.id));
      if (node) {
        used.add(node.id);
        const { error: upErr } = await supabase.from("skill_nodes").update({ position_index: j }).eq("id", node.id);
        if (upErr) return { error: upErr.message };
      }
    }
  }

  return saveOutlineOnly(projectId, outline);
}

/**
 * Remove a full section/chapter from the outline and delete its chapter + nodes (or topic-titled nodes if no chapters).
 */
export async function removeOutlineSection(projectId: string, sectionIndex: number): Promise<{ error?: string }> {
  const { data: row, error: rowErr } = await supabase
    .from("addie_outlines")
    .select("id, creation_outline")
    .eq("project_id", projectId)
    .maybeSingle();
  if (rowErr || !row?.creation_outline) return { error: rowErr?.message || "No outline found" };

  const outline = cloneJson(row.creation_outline) as Record<string, unknown>;
  const { sections, useModules } = getSectionsMut(outline);
  if (sectionIndex < 0 || sectionIndex >= sections.length) return { error: "Invalid section" };

  const removed = sections[sectionIndex];
  const removedTopicNorms = new Set(
    (Array.isArray(removed.topics) ? removed.topics : []).map((t) => normTitle(String(t ?? ""))).filter(Boolean),
  );

  const { data: treeData, error: treeErr } = await invokeFunction("get-skill-tree", { body: { courseId: projectId } });
  if (treeErr) return { error: treeErr.message };

  const chapters = (((treeData?.chapters ?? []) as { id: string; order_index: number }[]) || [])
    .slice()
    .sort((a, b) => a.order_index - b.order_index);
  const nodes = (treeData?.nodes ?? []) as { id: string; title: string; chapter_id: string | null }[];

  const chapterId = chapters[sectionIndex]?.id;

  if (chapterId) {
    const { data: inCh } = await supabase.from("skill_nodes").select("id").eq("chapter_id", chapterId);
    for (const n of inCh ?? []) {
      const { error: e } = await supabase.from("skill_nodes").delete().eq("id", (n as { id: string }).id);
      if (e) return { error: e.message };
    }
    const { error: chDelErr } = await sb.from("skill_tree_chapters").delete().eq("id", chapterId);
    if (chDelErr) return { error: chDelErr.message };

    const remaining = chapters.filter((c) => c.id !== chapterId).sort((a, b) => a.order_index - b.order_index);
    for (let i = 0; i < remaining.length; i++) {
      if (remaining[i].order_index !== i) {
        const { error: u } = await sb.from("skill_tree_chapters").update({ order_index: i }).eq("id", remaining[i].id);
        if (u) return { error: u.message };
      }
    }
  } else {
    for (const n of nodes) {
      if (removedTopicNorms.has(normTitle(n.title))) {
        const { error: e } = await supabase.from("skill_nodes").delete().eq("id", n.id);
        if (e) return { error: e.message };
      }
    }
  }

  sections.splice(sectionIndex, 1);
  writeSectionsMut(outline, sections, useModules);

  return saveOutlineOnly(projectId, outline);
}

/** Find outline topic matching this node title and remove it; otherwise delete the node only. */
export async function removeOutlineTopicByNodeId(projectId: string, nodeId: string): Promise<{ error?: string }> {
  const { data: treeData, error: treeErr } = await invokeFunction("get-skill-tree", { body: { courseId: projectId } });
  if (treeErr) return { error: treeErr.message };
  const nodes = (treeData?.nodes ?? []) as { id: string; title: string }[];
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) return { error: "Node not found" };

  const { data: row } = await supabase.from("addie_outlines").select("creation_outline").eq("project_id", projectId).maybeSingle();
  if (!row?.creation_outline) {
    const { error } = await supabase.from("skill_nodes").delete().eq("id", nodeId);
    return error ? { error: error.message } : {};
  }
  const outline = row.creation_outline as Record<string, unknown>;
  const { sections } = getSectionsMut(outline);
  const nt = normTitle(node.title);

  for (let si = 0; si < sections.length; si++) {
    const topics = Array.isArray(sections[si].topics) ? (sections[si].topics as string[]) : [];
    for (let ti = 0; ti < topics.length; ti++) {
      if (normTitle(String(topics[ti] ?? "")) === nt) {
        return removeOutlineTopic(projectId, si, ti);
      }
    }
  }

  const { error } = await supabase.from("skill_nodes").delete().eq("id", nodeId);
  return error ? { error: error.message } : {};
}

export async function removeOutlineSectionByChapterId(projectId: string, chapterId: string): Promise<{ error?: string }> {
  const { data: treeData, error: treeErr } = await invokeFunction("get-skill-tree", { body: { courseId: projectId } });
  if (treeErr) return { error: treeErr.message };
  const chapters = (((treeData?.chapters ?? []) as { id: string; order_index: number }[]) || [])
    .slice()
    .sort((a, b) => a.order_index - b.order_index);
  const idx = chapters.findIndex((c) => c.id === chapterId);
  if (idx < 0) return { error: "Chapter not found" };
  return removeOutlineSection(projectId, idx);
}
